import { signOut } from 'firebase/auth';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { auth, db } from '../config/firebase';
import { GarageSubmission, approveGarageSubmission, deleteGarageSubmission, rejectGarageSubmission } from '../services/onboardingService';

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState<GarageSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [selectedSubmission, setSelectedSubmission] = useState<GarageSubmission | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [pendingRejectionId, setPendingRejectionId] = useState<string | null>(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      console.log('🔍 Fetching garage submissions...');
      const q = query(collection(db, 'garageSubmissions'), orderBy('submittedAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const submissionsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as GarageSubmission[];
      console.log('✅ Fetched submissions:', submissionsData);
      setSubmissions(submissionsData);
    } catch (error) {
      console.error('❌ Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (submissionId: string) => {
    if (!auth.currentUser) {
      alert('You must be logged in to approve submissions');
      return;
    }

    setActionLoading(submissionId);
    try {
      console.log('✅ Approving submission:', submissionId);
      const result = await approveGarageSubmission(submissionId, auth.currentUser.email || 'admin');
      
      if (result.success) {
        alert(`✅ Garage approved successfully! Garage ID: ${result.garageId}`);
        await fetchSubmissions(); // Refresh the list
      } else {
        alert(`❌ Failed to approve garage: ${result.error}`);
      }
    } catch (error) {
      console.error('❌ Error approving garage:', error);
      alert('An error occurred while approving the garage');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (submissionId: string) => {
    if (!auth.currentUser) {
      alert('You must be logged in to reject submissions');
      return;
    }

    setPendingRejectionId(submissionId);
    setShowRejectionModal(true);
  };

  const confirmRejection = async () => {
    if (!pendingRejectionId || !auth.currentUser) return;

    setActionLoading(pendingRejectionId);
    try {
      console.log('❌ Rejecting submission:', pendingRejectionId);
      const result = await rejectGarageSubmission(
        pendingRejectionId, 
        auth.currentUser.email || 'admin',
        rejectionReason
      );
      
      if (result.success) {
        alert('✅ Garage rejected successfully');
        await fetchSubmissions(); // Refresh the list
      } else {
        alert(`❌ Failed to reject garage: ${result.error}`);
      }
    } catch (error) {
      console.error('❌ Error rejecting garage:', error);
      alert('An error occurred while rejecting the garage');
    } finally {
      setActionLoading(null);
      setShowRejectionModal(false);
      setPendingRejectionId(null);
      setRejectionReason('');
    }
  };

  const handleDelete = async (submissionId: string) => {
    if (!auth.currentUser) {
      alert('You must be logged in to delete submissions');
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      'Are you sure you want to delete this garage submission? This action cannot be undone and will also remove the garage from the main app if it was approved.'
    );

    if (!confirmed) return;

    setActionLoading(submissionId);
    try {
      console.log('🗑️ Deleting submission:', submissionId);
      const result = await deleteGarageSubmission(submissionId, auth.currentUser.email || 'admin');
      
      if (result.success) {
        alert('✅ Garage submission deleted successfully');
        await fetchSubmissions(); // Refresh the list
      } else {
        alert(`❌ Failed to delete garage: ${result.error}`);
      }
    } catch (error) {
      console.error('❌ Error deleting garage:', error);
      alert('An error occurred while deleting the garage');
    } finally {
      setActionLoading(null);
    }
  };



  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const filteredSubmissions = submissions.filter(submission => {
    const matchesSearch = 
      submission.garageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.contactEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.fullAddress.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || submission.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Loading submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Garage Submissions Admin</h1>
              <p className="text-gray-600">Review and approve/reject garage onboarding submissions</p>
            </div>
            <button
              onClick={handleSignOut}
              className="btn-secondary"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                placeholder="Search by name, email, or address..."
                className="input-field"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                className="input-field"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                {filteredSubmissions.length} of {submissions.length} submissions
              </div>
            </div>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Garage Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Review Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSubmissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {submission.garageName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {submission.fullAddress}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">
                          {submission.contactEmail}
                        </div>
                        <div className="text-sm text-gray-500">
                          {submission.phoneNumber || 'No phone'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">
                          {submission.spacesForParkRez} spaces
                        </div>
                        <div className="text-sm text-gray-500">
                          ${submission.ratePerHour}/hr
                        </div>
                        <div className="text-sm text-gray-500">
                          {submission.operatingHours}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        submission.status === 'approved' 
                          ? 'bg-green-100 text-green-800'
                          : submission.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {submission.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(submission.submittedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            console.log('👁️ Viewing submission:', submission);
                            setSelectedSubmission(submission);
                          }}
                          className="text-primary-600 hover:text-primary-900"
                          disabled={actionLoading === submission.id}
                        >
                          View
                        </button>
                        {submission.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(submission.id!)}
                              disabled={actionLoading === submission.id}
                              className="text-green-600 hover:text-green-900 disabled:opacity-50"
                            >
                              {actionLoading === submission.id ? 'Approving...' : 'Approve'}
                            </button>
                            <button
                              onClick={() => handleReject(submission.id!)}
                              disabled={actionLoading === submission.id}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                            >
                              {actionLoading === submission.id ? 'Rejecting...' : 'Reject'}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(submission.id!)}
                          disabled={actionLoading === submission.id}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          title="Delete this submission and remove from main app if approved"
                        >
                          {actionLoading === submission.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredSubmissions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No submissions found</p>
          </div>
        )}
      </div>

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Garage Submission</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rejection (Optional)
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="input-field w-full"
                  rows={3}
                  placeholder="Provide a reason for rejection..."
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRejectionModal(false);
                    setPendingRejectionId(null);
                    setRejectionReason('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRejection}
                  className="btn-primary bg-red-600 hover:bg-red-700"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submission Detail Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedSubmission.garageName}
                </h3>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Owner Details */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Owner Details</h4>
                  <dl className="space-y-1 text-sm">
                    <div>
                      <dt className="text-gray-500">Full Name:</dt>
                      <dd className="text-gray-900">{selectedSubmission.fullName}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Contact Email:</dt>
                      <dd className="text-gray-900">{selectedSubmission.contactEmail}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Phone:</dt>
                      <dd className="text-gray-900">{selectedSubmission.phoneNumber || 'Not provided'}</dd>
                    </div>
                    {selectedSubmission.businessName && (
                      <div>
                        <dt className="text-gray-500">Business Name:</dt>
                        <dd className="text-gray-900">{selectedSubmission.businessName}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                {/* Location & Identity */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Location & Identity</h4>
                  <dl className="space-y-1 text-sm">
                    <div>
                      <dt className="text-gray-500">Garage Name:</dt>
                      <dd className="text-gray-900">{selectedSubmission.garageName}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Address:</dt>
                      <dd className="text-gray-900">{selectedSubmission.fullAddress}</dd>
                    </div>
                    {selectedSubmission.googleMapsLink && (
                      <div>
                        <dt className="text-gray-500">Google Maps:</dt>
                        <dd className="text-gray-900">
                          <a href={selectedSubmission.googleMapsLink} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-500">
                            View on Maps
                          </a>
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-gray-500">Location Type:</dt>
                      <dd className="text-gray-900">{selectedSubmission.locationType}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Parking Type:</dt>
                      <dd className="text-gray-900">{selectedSubmission.parkingType}</dd>
                    </div>
                  </dl>
                </div>

                {/* Capacity & Rates */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Capacity & Rates</h4>
                  <dl className="space-y-1 text-sm">
                    <div>
                      <dt className="text-gray-500">Total Spaces:</dt>
                      <dd className="text-gray-900">{selectedSubmission.totalSpaces}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Spaces for ParkRez:</dt>
                      <dd className="text-gray-900">{selectedSubmission.spacesForParkRez}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Rate Per Hour:</dt>
                      <dd className="text-gray-900">${selectedSubmission.ratePerHour}</dd>
                    </div>
                    {selectedSubmission.dailyMaxRate > 0 && (
                      <div>
                        <dt className="text-gray-500">Daily Max Rate:</dt>
                        <dd className="text-gray-900">${selectedSubmission.dailyMaxRate}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-gray-500">Operating Hours:</dt>
                      <dd className="text-gray-900">{selectedSubmission.operatingHours}</dd>
                    </div>
                  </dl>
                </div>

                {/* Security & Restrictions */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Security & Restrictions</h4>
                  <dl className="space-y-1 text-sm">
                    {selectedSubmission.securityType && (
                      <div>
                        <dt className="text-gray-500">Security Type:</dt>
                        <dd className="text-gray-900">{selectedSubmission.securityType}</dd>
                      </div>
                    )}
                    {selectedSubmission.accessMethod && (
                      <div>
                        <dt className="text-gray-500">Access Method:</dt>
                        <dd className="text-gray-900">{selectedSubmission.accessMethod}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-gray-500">Overnight Parking:</dt>
                      <dd className="text-gray-900">{selectedSubmission.allowOvernightParking ? 'Yes' : 'No'}</dd>
                    </div>
                    {selectedSubmission.restrictions && (
                      <div>
                        <dt className="text-gray-500">Restrictions:</dt>
                        <dd className="text-gray-900">{selectedSubmission.restrictions}</dd>
                      </div>
                    )}
                    {selectedSubmission.amenities && (
                      <div>
                        <dt className="text-gray-500">Amenities:</dt>
                        <dd className="text-gray-900">{selectedSubmission.amenities}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                {/* Legal & Payout */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Legal & Payout</h4>
                  <dl className="space-y-1 text-sm">
                    {selectedSubmission.businessTaxId && (
                      <div>
                        <dt className="text-gray-500">Business Tax ID:</dt>
                        <dd className="text-gray-900">{selectedSubmission.businessTaxId}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-gray-500">Payout Email:</dt>
                      <dd className="text-gray-900">{selectedSubmission.payoutEmail}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Agreed to Terms:</dt>
                      <dd className="text-gray-900">{selectedSubmission.agreeToTerms ? 'Yes' : 'No'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Signature:</dt>
                      <dd className="text-gray-900">{selectedSubmission.signature}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Signature Date:</dt>
                      <dd className="text-gray-900">{formatDate(selectedSubmission.signatureDate)}</dd>
                    </div>
                  </dl>
                </div>

                {/* Status & Submission Info */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Status & Submission</h4>
                  <dl className="space-y-1 text-sm">
                    <div>
                      <dt className="text-gray-500">Status:</dt>
                      <dd className="text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          selectedSubmission.status === 'approved' 
                            ? 'bg-green-100 text-green-800'
                            : selectedSubmission.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {selectedSubmission.status}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Submitted:</dt>
                      <dd className="text-gray-900">{formatDate(selectedSubmission.submittedAt)}</dd>
                    </div>
                    {selectedSubmission.reviewedAt && (
                      <div>
                        <dt className="text-gray-500">Reviewed:</dt>
                        <dd className="text-gray-900">{formatDate(selectedSubmission.reviewedAt)}</dd>
                      </div>
                    )}
                    {selectedSubmission.reviewedBy && (
                      <div>
                        <dt className="text-gray-500">Reviewed By:</dt>
                        <dd className="text-gray-900">{selectedSubmission.reviewedBy}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>

              {/* Additional Information */}
              {selectedSubmission.specialInstructions && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-2">Additional Information & Special Instructions</h4>
                  <p className="text-sm text-gray-700">{selectedSubmission.specialInstructions}</p>
                </div>
              )}

              {/* Photos */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {selectedSubmission.garagePhotoUrl && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Garage Photo</h4>
                    <img
                      src={selectedSubmission.garagePhotoUrl}
                      alt="Garage"
                      className="w-full max-w-md h-48 object-cover rounded-lg"
                    />
                  </div>
                )}
                {selectedSubmission.lotSignagePhotoUrl && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Lot Signage Photo</h4>
                    <img
                      src={selectedSubmission.lotSignagePhotoUrl}
                      alt="Lot Signage"
                      className="w-full max-w-md h-48 object-cover rounded-lg"
                    />
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="btn-secondary"
                >
                  Close
                </button>
                {selectedSubmission.status === 'pending' && (
                  <>
                    <button
                      onClick={() => {
                        handleApprove(selectedSubmission.id!);
                        setSelectedSubmission(null);
                      }}
                      disabled={actionLoading === selectedSubmission.id}
                      className="btn-primary bg-green-600 hover:bg-green-700 disabled:opacity-50"
                    >
                      {actionLoading === selectedSubmission.id ? 'Approving...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => {
                        handleReject(selectedSubmission.id!);
                        setSelectedSubmission(null);
                      }}
                      disabled={actionLoading === selectedSubmission.id}
                      className="btn-primary bg-red-600 hover:bg-red-700 disabled:opacity-50"
                    >
                      {actionLoading === selectedSubmission.id ? 'Rejecting...' : 'Reject'}
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    handleDelete(selectedSubmission.id!);
                    setSelectedSubmission(null);
                  }}
                  disabled={actionLoading === selectedSubmission.id}
                  className="btn-primary bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {actionLoading === selectedSubmission.id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;



